// Defaults live here once so the loaded form, the dirty check and the blank state agree.
import { calculateRouteCost } from "@/lib/routeCostCalculation"
import { CollectionsConfig, DrivingRegime, VehicleType } from "@/types/collections"

export interface ConfigFormValues {
  driver_wage_rate_per_hour: number
  fleet_cost_per_month_gbp: number
  driver_max_hours_per_day: number
  haulage_min_empty_pct: number
  haulage_min_extended_minutes: number
  on_site_charging_cost_per_kwh: number
  default_cost_per_kwh: number
  oaklands_cost_per_kwh: number
  internal_charging_range_pct: number
  planned_mileage_percentage_add: number
  uk_pallet_ratio_sort_bag: number
  uk_pallet_ratio_cage: number
  uk_pallet_ratio_magnum: number
  uk_pallet_ratio_eur_pallet: number
  uk_pallet_ratio_uk_pallet: number
}

export const CONFIG_DEFAULTS: ConfigFormValues = {
  driver_wage_rate_per_hour: 0,
  fleet_cost_per_month_gbp: 0,
  driver_max_hours_per_day: 12,
  haulage_min_empty_pct: 10,
  haulage_min_extended_minutes: 60,
  on_site_charging_cost_per_kwh: 0,
  default_cost_per_kwh: 0,
  oaklands_cost_per_kwh: 0.305,
  internal_charging_range_pct: 70,
  planned_mileage_percentage_add: 0,
  uk_pallet_ratio_sort_bag: 3.43,
  uk_pallet_ratio_cage: 1.67,
  uk_pallet_ratio_magnum: 1.0,
  uk_pallet_ratio_eur_pallet: 1.25,
  uk_pallet_ratio_uk_pallet: 1.0,
}

/** Wage and off-site rate default on any falsy value; the rest only when missing. */
export function formFromConfig(config: CollectionsConfig): ConfigFormValues {
  const d = CONFIG_DEFAULTS
  return {
    driver_wage_rate_per_hour: config.driver_wage_rate_per_hour || d.driver_wage_rate_per_hour,
    fleet_cost_per_month_gbp: config.fleet_cost_per_month_gbp ?? d.fleet_cost_per_month_gbp,
    driver_max_hours_per_day: config.driver_max_hours_per_day ?? d.driver_max_hours_per_day,
    haulage_min_empty_pct: config.haulage_min_empty_pct ?? d.haulage_min_empty_pct,
    haulage_min_extended_minutes:
      config.haulage_min_extended_minutes ?? d.haulage_min_extended_minutes,
    on_site_charging_cost_per_kwh:
      config.on_site_charging_cost_per_kwh ?? d.on_site_charging_cost_per_kwh,
    default_cost_per_kwh: config.default_cost_per_kwh || d.default_cost_per_kwh,
    oaklands_cost_per_kwh: config.oaklands_cost_per_kwh ?? d.oaklands_cost_per_kwh,
    internal_charging_range_pct:
      config.internal_charging_range_pct ?? d.internal_charging_range_pct,
    planned_mileage_percentage_add:
      config.planned_mileage_percentage_add ?? d.planned_mileage_percentage_add,
    uk_pallet_ratio_sort_bag: config.uk_pallet_ratio_sort_bag ?? d.uk_pallet_ratio_sort_bag,
    uk_pallet_ratio_cage: config.uk_pallet_ratio_cage ?? d.uk_pallet_ratio_cage,
    uk_pallet_ratio_magnum: config.uk_pallet_ratio_magnum ?? d.uk_pallet_ratio_magnum,
    uk_pallet_ratio_eur_pallet: config.uk_pallet_ratio_eur_pallet ?? d.uk_pallet_ratio_eur_pallet,
    uk_pallet_ratio_uk_pallet: config.uk_pallet_ratio_uk_pallet ?? d.uk_pallet_ratio_uk_pallet,
  }
}

/** Wage and off-site rate compare against the raw stored value, so either missing always reads dirty. */
export function configFormIsDirty(form: ConfigFormValues, config: CollectionsConfig): boolean {
  const saved = formFromConfig(config)
  return (
    form.driver_wage_rate_per_hour !== config.driver_wage_rate_per_hour ||
    form.default_cost_per_kwh !== config.default_cost_per_kwh ||
    (Object.keys(saved) as (keyof ConfigFormValues)[]).some(
      (key) =>
        key !== "driver_wage_rate_per_hour" &&
        key !== "default_cost_per_kwh" &&
        form[key] !== saved[key],
    )
  )
}

/** The Cost tab's example route cost, using unsaved form values when `useUnsaved`. */
export function exampleRouteCost(
  vehicleType: VehicleType,
  config: CollectionsConfig | undefined,
  form: ConfigFormValues,
  useUnsaved: boolean,
  distanceMiles: number = 350,
  durationMinutes: number = 480,
): { dailyLeaseCost: string; chargingCost: string; driverWageCost: string; totalCost: string } {
  const effectiveConfig = config
    ? {
        ...config,
        ...(useUnsaved
          ? {
              driver_wage_rate_per_hour: form.driver_wage_rate_per_hour,
              driver_max_hours_per_day: form.driver_max_hours_per_day,
              on_site_charging_cost_per_kwh: form.on_site_charging_cost_per_kwh,
              default_cost_per_kwh: form.default_cost_per_kwh,
              oaklands_cost_per_kwh: form.oaklands_cost_per_kwh,
              internal_charging_range_pct: form.internal_charging_range_pct,
            }
          : {}),
      }
    : undefined
  const cost = calculateRouteCost({
    vehicleType,
    distanceMiles,
    durationMinutes,
    config: effectiveConfig ?? null,
    totalForecastVolume: null,
  })
  if (!cost) {
    return {
      dailyLeaseCost: "0.00",
      chargingCost: "0.00",
      driverWageCost: "0.00",
      totalCost: "0.00",
    }
  }
  return {
    dailyLeaseCost: cost.vehicle_lease_cost.toFixed(2),
    chargingCost: cost.charging_cost.toFixed(2),
    driverWageCost: cost.driver_wage_cost.toFixed(2),
    totalCost: cost.total_cost.toFixed(2),
  }
}

export interface VehicleTypeFormData {
  name: string
  display_name?: string
  monthly_lease_cost: number
  miles_per_kwh: number
  labour_hourly_rate: number
  range_miles: number
  eur_pallet_capacity?: number
  uk_pallet_capacity?: number
  cage_capacity?: number
  magnum_capacity?: number
  sort_bag_capacity?: number
  // Null means unknown.
  driving_regime: DrivingRegime | null
  height_m: number | null
  gross_weight_kg: number | null
  is_active: boolean
}

export const EMPTY_VEHICLE_TYPE_FORM: VehicleTypeFormData = {
  name: "",
  display_name: "",
  monthly_lease_cost: 0,
  miles_per_kwh: 0,
  labour_hourly_rate: 0,
  range_miles: 100,
  eur_pallet_capacity: 0,
  uk_pallet_capacity: 0,
  cage_capacity: 0,
  magnum_capacity: 0,
  sort_bag_capacity: 0,
  driving_regime: null,
  height_m: null,
  gross_weight_kg: null,
  is_active: true,
}

export function vehicleTypeFormFrom(vehicleType: VehicleType): VehicleTypeFormData {
  return {
    name: vehicleType.name,
    display_name: vehicleType.display_name || "",
    monthly_lease_cost: vehicleType.monthly_lease_cost,
    miles_per_kwh: vehicleType.miles_per_kwh,
    labour_hourly_rate: vehicleType.labour_hourly_rate ?? 0,
    range_miles: vehicleType.range_miles ?? 100,
    eur_pallet_capacity: vehicleType.eur_pallet_capacity || 0,
    uk_pallet_capacity: vehicleType.uk_pallet_capacity || 0,
    cage_capacity: vehicleType.cage_capacity || 0,
    magnum_capacity: vehicleType.magnum_capacity || 0,
    sort_bag_capacity: vehicleType.sort_bag_capacity || 0,
    driving_regime: vehicleType.driving_regime ?? null,
    height_m: vehicleType.height_m ?? null,
    gross_weight_kg: vehicleType.gross_weight_kg ?? null,
    is_active: vehicleType.is_active,
  }
}
